<script setup>
/*
 * Én visning til alle fem produktliste-blokke.
 *
 * edbpriser havde fem naesten identiske kodeblokke paa i alt godt tusind
 * linjer, hvor det eneste forskellige var HVORDAN varerne blev valgt.
 * Udvaelgelsen sker paa serveren; her tegner vi bare listen.
 *
 * DEN TAETTE LISTE ER EN FUNKTION. "Kaempe billeder plastret ud over hele
 * skaermen" var dét der drev folk vaek fra baade edbpriser og
 * PriceRunner. Gitteret er standard, men listevisningen er der for dem
 * der vil have flere varer paa skaermen.
 */
import ProductGridCard from '@/Components/Product/ProductGridCard.vue';
import ProductListRow from '@/Components/Product/ProductListRow.vue';
import { computed, ref } from 'vue';

const props = defineProps({
    data: { type: Object, required: true },
});

const erGitter = computed(() => (props.data.visning || 'gitter') === 'gitter');

/*
 * CHIPS, SOM edbprisers BASHERS.
 *
 * Værdierne kommer fra serveren og er talt på de varer der faktisk ligger
 * i listen, så en chip aldrig lover et træf den ikke kan holde.
 *
 * Der filtreres her og ikke på serveren: listen er højst otteogfyrre varer
 * og ligger der allerede, så en rundtur ville kun gøre den langsommere.
 */
const filtre = computed(() => props.data.filtre || {});
const harFiltre = computed(() => Object.keys(filtre.value).length > 0);

const valgt = ref({});

function vaelg(dimension, vaerdi) {
    valgt.value = { ...valgt.value, [dimension]: valgt.value[dimension] === vaerdi ? null : vaerdi };
}

function vaerdiAf(vare, dimension) {
    return dimension === 'maerke' ? vare.brand?.name : vare.kategori_navn;
}

const varer = computed(() => {
    const alle = props.data.varer || [];

    return alle.filter((v) => Object.entries(valgt.value).every(
        ([dimension, vaerdi]) => !vaerdi || vaerdiAf(v, dimension) === vaerdi,
    ));
});

const ETIKET = { kategori: 'Kategori', maerke: 'Mærke' };
</script>

<template>
    <div v-if="harFiltre" class="blok-chips mb-3">
        <div v-for="(vaerdier, dimension) in filtre" :key="dimension" class="blok-chips__raekke">
            <span class="blok-chips__etiket">{{ ETIKET[dimension] || dimension }}</span>

            <button
                v-for="p in vaerdier"
                :key="p.vaerdi"
                type="button"
                class="blok-chip"
                :class="{ 'blok-chip--valgt': valgt[dimension] === p.vaerdi }"
                :aria-pressed="valgt[dimension] === p.vaerdi"
                @click="vaelg(dimension, p.vaerdi)"
            >
                {{ p.vaerdi }} <span class="blok-chip__antal">{{ p.antal }}</span>
            </button>
        </div>
    </div>

    <p v-if="!varer.length" class="text-secondary small mb-0">
        Ingen varer matcher lige nu.
    </p>

    <div v-else-if="erGitter" class="blok-gitter">
        <div v-for="(vare, i) in varer" :key="vare.pricecompare_id || vare.slug || i" class="blok-gitter__felt">
            <span v-if="data.nummerering" class="blok-nr">{{ i + 1 }}</span>

            <!--
                Faldet som en stor cirkel, og kategorien som en etiket.
                Begge er laant fra edbprisers egen Black Friday-side: uden
                dem er en prisfalds-liste bare en raekke ens kasser, og
                laeseren kan hverken se HVOR MEGET eller HVAD det er.
            -->
            <span v-if="vare.kategori_navn" class="blok-kat">{{ vare.kategori_navn }}</span>
            <span v-if="vare.fald_procent" class="blok-fald">−{{ vare.fald_procent }}<span class="blok-fald__tegn">%</span></span>

            <ProductGridCard :product="vare" />
        </div>
    </div>

    <div v-else class="blok-liste">
        <div v-for="(vare, i) in varer" :key="vare.pricecompare_id || vare.slug || i" class="blok-liste__raekke">
            <span v-if="data.nummerering" class="blok-nr blok-nr--liste">{{ i + 1 }}</span>
            <ProductListRow :product="vare" />
        </div>
    </div>
</template>

<style scoped>
.blok-chips { display: flex; flex-direction: column; gap: .5rem; }
.blok-chips__raekke { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem; }
.blok-chips__etiket {
    font-size: .75rem; font-weight: 600; letter-spacing: .02em;
    color: var(--pc-text-secondary); margin-right: .25rem;
}
.blok-chip {
    border: 1px solid var(--pc-border);
    background: var(--pc-bg-secondary);
    color: var(--pc-text-primary);
    border-radius: var(--pc-radius-full, 999px);
    padding: .2rem .7rem;
    font-size: .8125rem;
    cursor: pointer;
    transition: all var(--pc-transition, .15s);
}
.blok-chip:hover { border-color: var(--pc-border-hover); }
.blok-chip--valgt {
    background: var(--pc-accent-solid);
    border-color: var(--pc-accent-solid);
    color: var(--pc-on-accent);
}
.blok-chip__antal { opacity: .65; font-variant-numeric: tabular-nums; }

.blok-gitter { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); }
.blok-gitter__felt { position: relative; }
.blok-liste__raekke { position: relative; }
.blok-nr {
    position: absolute; top: .25rem; left: .25rem; z-index: 2;
    background: var(--pc-accent); color: var(--pc-on-accent, #0A0C10);
    border-radius: 999px; min-width: 1.5rem; height: 1.5rem;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: .75rem; font-weight: 600; padding: 0 .35rem;
}
.blok-nr--liste { top: 50%; transform: translateY(-50%); }

.blok-kat {
    position: absolute; top: .3rem; left: 50%; transform: translateX(-50%); z-index: 2;
    background: var(--pc-bg-tertiary); color: var(--pc-text-secondary);
    border-radius: var(--pc-radius-full); padding: .05rem .5rem;
    font-size: .68rem; white-space: nowrap; max-width: 90%;
    overflow: hidden; text-overflow: ellipsis;
}
.blok-fald {
    position: absolute; top: .3rem; right: .3rem; z-index: 2;
    width: 2.6rem; height: 2.6rem; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--pc-price); color: var(--pc-on-accent, #0A0C10);
    font-weight: 800; font-size: .95rem; letter-spacing: -.02em;
    box-shadow: var(--pc-shadow-sm, 0 1px 3px rgba(0,0,0,.3));
}
.blok-fald__tegn { font-size: .6rem; margin-left: .05rem; align-self: flex-start; margin-top: .45rem; }
</style>
